import React, { useState, forwardRef } from 'react';
import {
  TextField,
  Button,
  Box,
  Grid,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Slide,
  FormHelperText
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { TransitionProps } from '@mui/material/transitions';
import { theme } from '../../../../../theme';
import { validateEmail } from '../../../Validation/validation';
import imageCompression from 'browser-image-compression';
import { checkUseridExists } from '../userApi';
import { useParams } from 'react-router-dom';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (newUser: any) => void;
  existingUserIds: string[];
}

const AddNewUser: React.FC<Props> = ({ open, onClose, onSubmit, existingUserIds }) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [formData, setFormData] = useState({
    userid: '',
    username: '',
    email: '',
    role: '',
    status: '',
    photo: null as File | null,
    photoURL: '', // URL for photo preview
  });
  const { tenant } = useParams<{ tenant: string }>();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [event.target.name]: ''
    }))
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      status: event.target.value,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      status: ''
    }))
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      role: event.target.value,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      role: ''
    }))
  };

  const handleAvatarClick = () => {
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      // Compress
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.05, // 0.05 MB = 50 KB
        maxWidthOrHeight: 300,
        useWebWorker: true,
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          photo: compressedFile,
          photoURL: reader.result as string,
        }));
      };
      reader.readAsDataURL(compressedFile);
    }
  };

  const handleSubmit = async () => {
    const newErrors: { [key: string]: string } = {};
    // Username validation
    const username = formData.username.trim();
    if (!username) {
      newErrors.username = 'Username is required.';
    } else if (username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters.';
    } else if (username.length > 50) {
      newErrors.username = 'Username must be at most 50 characters.';
    }

    // Email validation
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    // Role and status validation
    if (!formData.role.trim()) {
      newErrors.role = 'Role is required.';
    }
    if (!formData.status.trim()) {
      newErrors.status = 'Status is required.';
    }

    // User ID validation
    const userId = formData.userid.trim();
    if (!userId) {
      newErrors.userid = 'User ID is required.';
    } else if (userId.length < 3) {
      newErrors.userid = 'User ID must be at least 3 characters.';
    } else if (userId.length > 50) {
      newErrors.userid = 'User ID must be at most 50 characters.';
    }


    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return
    }
    const validateUserId = await handleUserIdBlur()

    if (!validateUserId) {
      return
    }
    onSubmit(formData);  // Pass formData including the photo back to parent
    setFormData({
      userid: '',
      username: '',
      email: '',
      role: '',
      status: '',
      photo: null,
      photoURL: '',
    });
    setErrors({});
    onClose()
  };

  const handleUserIdBlur = async () => {
    const userId = formData.userid.trim();

    if (!userId) {
      return false;;
    }

    try {
      const result = await checkUseridExists(tenant, { UserID: userId, CheckFor: "register" });

      if (!result) {
        setErrors(prev => ({
          ...prev,
          userid: 'This User ID already exists. Please choose a different one.',
        }));
        return false;
      }
      return true;
    } catch (error) {
      console.error("User ID check failed:", error);
      setErrors(prev => ({
        ...prev,
        userid: 'Error validating User ID. Please try again later.',
      }));
      return false;
    }
  };


  return (
    <Dialog
      open={open}
      onClose={() => {}}
      TransitionComponent={Transition}
      fullScreen
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)",}}}
      PaperProps={{
        sx: {
          width: '22vw',

          height: '100vh',
          margin: 0,
          overflow: 'hidden',
          position: 'absolute',
          right: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '1.2vw 1vw 0 1vw',
          minHeight: '4vw',
          width: '100%'
        }}
      >
        {/* Left side: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonAddAltOutlinedIcon
            sx={{
              border: '0.0694vw solid #EEEEEE',
              bgcolor: theme.palette.secondary.light,
              color: theme.palette.secondary.main,
              marginRight: '1.11vw',
              borderRadius: '0.56vw',
              width: '2.5vw',
              height: '2.5vw',
              padding: '0.2vw',
            }}
          />

          <Typography variant="h6" fontWeight={600}>
            Add New User
          </Typography>
        </Box>

        {/* Close Icon */}
        <IconButton
          edge="end"
          sx={{
            padding: '0.3vw',
            mt: '0.1vw',
          }}
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>


      <Divider sx={{ mb: 1.2, mt: 2 }} />

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography gutterBottom variant='subtitle2'>Profile picture</Typography>
            <IconButton
              onClick={handleAvatarClick}
              disableRipple
              disableTouchRipple
              sx={{
                padding: 0, // Optional: Remove padding for a tighter fit around the avatar
              }}
            >
              <Avatar
                sx={{
                  width: '3.6111vw',
                  height: '3.6111vw',
                  bgcolor: theme.palette.background.paper,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!formData.photoURL ? (
                  <span
                    className="fa-regular fa-circle-user fa-3x"
                    style={{
                      color: theme.palette.secondary.main,
                    }}
                  />
                ) : (
                  <img
                    src={formData.photoURL}
                    alt="User Avatar"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </Avatar>
            </IconButton>
            <Divider sx={{ mb: 1.2, mt: 2 }} />
          </Grid>

          <Grid item xs={12} mb={'0.8vw'}>
            <TextField
              size="small"
              fullWidth
              label="User ID"
              name="userid"
              value={formData.userid}
              onChange={handleChange}
              onBlur={handleUserIdBlur}
              variant="outlined"
              required
              error={!!errors.userid}
              helperText={errors.userid}
              autoComplete='off'
            />
          </Grid>

          <Grid item xs={12}  mb={'0.8vw'}>
            <TextField
              size="small"
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              variant="outlined"
              required
              error={!!errors.username}
              helperText={errors.username}
              autoComplete='off'
            />
          </Grid>

          <Grid item xs={12}  mb={'0.8vw'}>
            <TextField
              size="small"
              fullWidth
              label="Email Address"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => {
                if (formData.email && !validateEmail(formData.email)) {
                  setErrors((prev) => ({ ...prev, email: 'Invalid email address' }));
                }
              }}
              variant="outlined"
              required
              error={!!errors.email}
              helperText={errors.email}
              autoComplete='off'
            />
          </Grid>

          {/* <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              variant="outlined"
              required
              error={!!errors.password}
              helperText={errors.password}
              autoComplete='off'
            />
          </Grid> */}



          <Grid item xs={12}  mb={'0.8vw'}>
            <FormControl fullWidth size="small" required error={!!errors.role}>
              <InputLabel>Role</InputLabel>
              <Select value={formData.role} onChange={handleRoleChange} label="Role">
                <MenuItem value="HR">HR</MenuItem>
                <MenuItem value="Recruiter">Recruiter</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Super Admin">Super Admin</MenuItem>
              </Select>
              {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}  mb={'0.8vw'}>
            <FormControl fullWidth size="small" required error={!!errors.status}>
              <InputLabel>Status</InputLabel>
              <Select value={formData.status} onChange={handleStatusChange} label="Status">
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="In-Active">In-Active</MenuItem>
              </Select>
              {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>

        {/* Hidden file input for photo upload */}
        <input
          id="file-upload"
          type="file"
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleFileChange}
          required
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ padding: '1.2vw 1vw' }}>
        <Button onClick={handleSubmit} variant="contained" color="primary" fullWidth>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewUser;
